// Spotter — site-wide behaviour: cart drawer, add to cart, quantity inputs.
// Loaded once per page as a deferred module. Page-specific code lives in its own file.

const S = window.Spotter || {};
const DRAWER_SECTION = 'cart-drawer';

export function formatMoney(cents, format = S.moneyFormat || '${{amount}}') {
  const value = Number(cents) / 100;
  const fmt = (n, decimals, thousands = ',', decimal = '.') => {
    const [int, frac] = n.toFixed(decimals).split('.');
    return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals': return fmt(value, 0);
      case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
      default: return fmt(value, 2);
    }
  });
}

/* ---------- Cart API ---------- */

async function cartRequest(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: body instanceof FormData ? { Accept: 'application/json' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: body instanceof FormData ? body : JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.status) {
    const err = new Error(data.description || data.message || S.strings?.cartError || 'Something went wrong.');
    err.data = data;
    throw err;
  }
  return data;
}

export const cart = {
  async add(formData) {
    formData.append('sections', DRAWER_SECTION);
    formData.append('sections_url', window.location.pathname);
    const data = await cartRequest(S.routes.cartAdd + '.js', formData);
    renderDrawer(data.sections);
    return data;
  },
  async change(line, quantity) {
    const data = await cartRequest(S.routes.cartChange + '.js', {
      line, quantity, sections: [DRAWER_SECTION], sections_url: window.location.pathname,
    });
    renderDrawer(data.sections);
    return data;
  },
  async updateNote(note) {
    return cartRequest(S.routes.cartUpdate + '.js', { note });
  },
};

function renderDrawer(sections) {
  const html = sections && sections[DRAWER_SECTION];
  if (!html) return;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const fresh = doc.querySelector('[data-cart-drawer-inner]');
  const current = document.querySelector('[data-cart-drawer-inner]');
  if (fresh && current) current.replaceWith(fresh);
  const count = fresh?.dataset.itemCount;
  if (count !== undefined) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = count; });
  }
  document.dispatchEvent(new CustomEvent('cart:updated', { detail: { itemCount: Number(count) } }));
}

/* ---------- Cart drawer (<dialog>) ---------- */

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.dialog.addEventListener('click', (e) => {
      if (e.target === this.dialog || e.target.closest('[data-cart-close]')) this.close();
    });
    this.addEventListener('click', (e) => this.onClick(e));
    this.addEventListener('change', (e) => this.onChange(e));
    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-cart-open]');
      if (!opener || e.metaKey || e.ctrlKey) return;
      e.preventDefault();
      this.open(opener);
    });
  }
  open(opener) {
    this.opener = opener || document.activeElement;
    if (!this.dialog.open) this.dialog.showModal();
    document.documentElement.style.overflow = 'hidden';
    this.dialog.addEventListener('close', () => {
      document.documentElement.style.overflow = '';
      this.opener?.focus?.();
    }, { once: true });
  }
  close() { this.dialog.close(); }
  async onClick(e) {
    const btn = e.target.closest('[data-line-change]');
    if (!btn) return;
    e.preventDefault();
    await this.changeLine(btn.dataset.line, Number(btn.dataset.quantity), btn);
  }
  async onChange(e) {
    if (e.target.matches('[data-cart-note]')) {
      cart.updateNote(e.target.value).catch(() => {});
    }
  }
  async changeLine(line, quantity, trigger) {
    const inner = this.querySelector('[data-cart-drawer-inner]');
    inner?.setAttribute('aria-busy', 'true');
    try {
      await cart.change(Number(line), quantity);
    } catch (err) {
      this.showError(err.message);
    } finally {
      this.querySelector('[data-cart-drawer-inner]')?.removeAttribute('aria-busy');
      // Keep keyboard focus inside the refreshed drawer.
      this.querySelector(`[data-line-change][data-line="${line}"]`)?.focus() || this.querySelector('[data-cart-close]')?.focus();
    }
  }
  showError(message) {
    const el = this.querySelector('[data-cart-error]');
    if (el) { el.textContent = message; el.hidden = false; }
  }
}
customElements.define('cart-drawer', CartDrawer);

/* ---------- Product form (add to cart) ---------- */

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.form?.addEventListener('submit', (e) => this.onSubmit(e));
    this.errorEl = this.querySelector('[data-form-error]');
  }
  get submitButtons() {
    // The sticky buy bar submits the same form via form="…", so include every submitter.
    return [...document.querySelectorAll(`[type="submit"][form="${this.form.id}"]`), ...this.form.querySelectorAll('[type="submit"]')];
  }
  async onSubmit(e) {
    e.preventDefault();
    if (this.busy) return;
    const buttons = this.submitButtons;
    if (buttons.some((b) => b.getAttribute('aria-disabled') === 'true')) return;
    this.busy = true;
    buttons.forEach((b) => { b.setAttribute('aria-busy', 'true'); b.disabled = true; b.dataset.label = b.innerHTML; b.innerHTML = '<span class="spinner" aria-hidden="true"></span><span>' + (b.dataset.busyLabel || 'Adding…') + '</span>'; });
    if (this.errorEl) this.errorEl.hidden = true;
    try {
      await cart.add(new FormData(this.form));
      document.querySelector('cart-drawer')?.open(e.submitter);
    } catch (err) {
      if (this.errorEl) { this.errorEl.textContent = err.message; this.errorEl.hidden = false; }
    } finally {
      buttons.forEach((b) => { b.removeAttribute('aria-busy'); b.disabled = false; if (b.dataset.label) b.innerHTML = b.dataset.label; });
      this.busy = false;
    }
  }
}
customElements.define('product-form', ProductForm);

/* ---------- Quantity input (− n +) ---------- */

class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('button[name]');
      if (!btn) return;
      e.preventDefault();
      const step = Number(this.input.step) || 1;
      const min = Number(this.input.min) || 1;
      const max = this.input.max ? Number(this.input.max) : Infinity;
      const next = Number(this.input.value) + (btn.name === 'plus' ? step : -step);
      this.input.value = Math.min(max, Math.max(min, next));
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
      this.update();
    });
    this.input.addEventListener('change', () => this.update());
    this.update();
  }
  update() {
    const v = Number(this.input.value);
    const minus = this.querySelector('button[name="minus"]');
    const plus = this.querySelector('button[name="plus"]');
    if (minus) minus.disabled = v <= (Number(this.input.min) || 1);
    if (plus) plus.disabled = this.input.max ? v >= Number(this.input.max) : false;
  }
}
customElements.define('quantity-input', QuantityInput);
